import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { CalendarDays, Users, X, User, RotateCcw } from "lucide-react";
import { drName, formatDateTimeParts, statusLabel } from "@/lib/utils";
import { useApp } from "@/context/AppContext";

export default function PatientDashboard() {
  const { lang } = useApp();
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [apts, setApts] = useState([]);

  const load = () => {
    api.get("/patient/me").then(r => setMe(r.data));
    api.get("/patient/appointments").then(r => setApts(r.data));
  };
  useEffect(() => { load(); }, []);

  const cancel = async (id) => {
    try { await api.post(`/patient/appointments/${id}/cancel`); toast.success("Cita cancelada"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const removeFam = async (fn, ln) => {
    await api.delete(`/patient/family/${encodeURIComponent(fn)}/${encodeURIComponent(ln)}`);
    toast.success("Perfil eliminado"); load();
  };

  if (!me) return <div className="max-w-3xl mx-auto p-8 text-slate-500">Cargando…</div>;

  const now = new Date();
  const upcoming = apts.filter(a => a.status !== "cancelled" && new Date(a.date + "T" + a.time) >= now);
  const past = apts.filter(a => a.status === "cancelled" || new Date(a.date + "T" + a.time) < now);

  return (
    <div className="max-w-4xl mx-auto px-4 lg:px-8 py-10">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-11 h-11 rounded-xl bg-blue-600 flex items-center justify-center">
          <User size={22} className="text-white"/>
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Mi espacio</h1>
          <p className="text-slate-500 text-sm">{me.email}</p>
        </div>
      </div>

      <Tabs defaultValue="appointments">
        <TabsList className="grid grid-cols-2 max-w-sm bg-slate-100">
          <TabsTrigger data-testid="patient-tab-apts" value="appointments">Mis citas</TabsTrigger>
          <TabsTrigger data-testid="patient-tab-family" value="family">Familiares</TabsTrigger>
        </TabsList>

        <TabsContent value="appointments" className="mt-5 space-y-4">
          <Card><CardContent className="p-5">
            <h2 className="font-semibold text-slate-900 mb-3 flex items-center gap-2"><CalendarDays size={18}/> Próximas citas</h2>
            {upcoming.length === 0 ? <p className="text-slate-500 text-sm">No tienes citas próximas.</p> :
              <div className="space-y-2">
                {upcoming.map(a => (
                  <div key={a.id} data-testid={`patient-apt-${a.id}`} className="p-3 rounded-xl border border-slate-200 hover:bg-slate-50">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div>
                        <div className="font-semibold text-slate-900">{drName(a.doctor_name)}</div>
                        <div className="text-xs text-slate-500">{a.specialty} · {a.clinic_name}</div>
                        <div className="text-xs text-slate-500">{a.address}</div>
                        {a.for_patient && (
                          <div className="text-xs text-blue-600 mt-1">Para {a.for_patient.first_name} {a.for_patient.last_name} ({a.for_patient.relationship})</div>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-semibold text-slate-800">{formatDateTimeParts(a.date, a.time)}</div>
                        <Button data-testid={`patient-cancel-${a.id}`} size="sm" variant="outline"
                                onClick={() => cancel(a.id)}
                                className="mt-2 border-red-200 text-red-600 hover:bg-red-50 h-8">
                          <X size={14} className="mr-1"/> Cancelar
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>}
          </CardContent></Card>

          <Card><CardContent className="p-5">
            <h2 className="font-semibold text-slate-900 mb-3">Historial</h2>
            {past.length === 0 ? <p className="text-slate-500 text-sm">—</p> :
              <div className="space-y-2">
                {past.slice(0, 30).map(a => (
                  <div key={a.id} className="flex items-center justify-between p-3 rounded-xl border border-slate-100 gap-2">
                    <div className="text-sm text-slate-700 flex-1 min-w-0">
                      <div className="truncate">{drName(a.doctor_name)} <span className="text-slate-400">· {formatDateTimeParts(a.date, a.time)}</span></div>
                    </div>
                    <Badge variant="outline" className="text-xs">{statusLabel(a.status, lang)}</Badge>
                    <Button data-testid={`book-again-${a.id}`} size="sm" variant="outline"
                            onClick={() => nav(`/doctor/${a.doctor_id}`)}
                            className="h-8 border-blue-200 text-blue-700 hover:bg-blue-50">
                      <RotateCcw size={12} className="mr-1"/> Reservar otra vez
                    </Button>
                  </div>
                ))}
              </div>}
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="family" className="mt-5">
          <Card><CardContent className="p-5">
            <h2 className="font-semibold text-slate-900 mb-3 flex items-center gap-2"><Users size={18}/> Familiares guardados</h2>
            {me.family.length === 0 ? <p className="text-slate-500 text-sm">Aún no has reservado para un familiar. Al hacerlo, su perfil se guarda aquí.</p> :
              <div className="space-y-2">
                {me.family.map((f, i) => (
                  <div key={i} data-testid={`family-${f.first_name}-${f.last_name}`}
                       className="flex items-center justify-between p-3 rounded-xl border border-slate-200">
                    <div>
                      <div className="font-medium text-slate-900">{f.first_name} {f.last_name}</div>
                      <div className="text-xs text-slate-500 capitalize">{f.relationship} · {f.dob}</div>
                    </div>
                    <Button data-testid={`family-remove-${f.first_name}`} size="sm" variant="outline"
                            onClick={() => removeFam(f.first_name, f.last_name)}
                            className="border-red-200 text-red-600 hover:bg-red-50 h-8">
                      Eliminar
                    </Button>
                  </div>
                ))}
              </div>}
          </CardContent></Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
